// Pohled okna podle druhu z kontraktu: list → ListView, record → RecordView, console → ConsoleView (kap. 38),
// custom → registrovaná komponenta aplikace.
import type { WindowDescriptor, CustomDescriptor } from '../contract';
import type { IwWindow, WindowView } from '../wm/window';
import type { WindowManager } from '../wm/manager';
import { ListView } from './list';
import { RecordView } from './record';
import { ConsoleView } from './console';
import { h } from '../dom';
import { errorState } from '../components/misc';

export interface ViewHost {
  close(result?: unknown): void;
  result(r: unknown): void;
  /** pohled je vložený v konzoli (vlevo už je strom): seznam bez vlastního levého panelu */
  embedded?: boolean;
  /** pohled je v záložce záznamu: místo pásu karet jeden řádek nástrojů (pás karet má záznam nad ním) */
  inline?: boolean;
  /** pohled je detail v pravém podokně seznamu (ovládání bar): lišta v podokně, okno si nepřejmenovává */
  pane?: boolean;
  /** detail v podokně posílá své akce (Upravit, Smazat… / Uložit, Zahodit změny) do záhlaví okna seznamu */
  paneItems?: (items: import('../components/commandbar').BarItem[], quick?: string[]) => void;
}

export interface CustomContext {
  desc: CustomDescriptor;
  win: IwWindow;
  wm: WindowManager;
  host: ViewHost;
}

export type CustomFactory = (ctx: CustomContext) => WindowView;

const customs = new Map<string, CustomFactory>();

/** Aplikace registruje vlastní komponentu okna pod názvem z CustomWindow::component(). */
export function registerComponent(name: string, factory: CustomFactory): void {
  customs.set(name, factory);
}

export function createView(desc: WindowDescriptor, win: IwWindow, wm: WindowManager, host: ViewHost): WindowView {
  // osobní volba „klasické ovládání“ (Přizpůsobit): seznamy a záznamy s pásem karet místo lišty v záhlaví
  const prefs = (window as unknown as { iwanPrefs?: Record<string, unknown> }).iwanPrefs ?? {};
  if (prefs['ui.chrome'] === 'ribbon' && (desc.kind === 'list' || desc.kind === 'record')) desc = { ...desc, chrome: 'ribbon' };
  switch (desc.kind) {
    case 'list':
      return new ListView(desc, win, wm, host);
    case 'record':
      return new RecordView(desc, win, wm, host);
    case 'console':
      return new ConsoleView(desc, win, wm, host);
    case 'custom': {
      const f = customs.get(desc.component);
      if (!f) return { el: h('div', { class: 'view' }, errorState(`Komponenta ${desc.component} není k dispozici.`)) };
      return f({ desc, win, wm, host });
    }
  }
}
